import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { CustomerFormType, DuplicateRecord } from "./CustomerFormTypes";
import { useImpersonationGuard } from "@/hooks/useImpersonationGuard";
import { customerFormSchema } from "./useCustomerFormValidation";

export function useCustomerForm(customer: any, onSave: () => void) {
  const [companyMembers, setCompanyMembers] = useState<any[]>([]);
  const [duplicateRecord, setDuplicateRecord] = useState<DuplicateRecord | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, profile } = useAuth();
  const notify = useNotify();
  const { guardWrite } = useImpersonationGuard();

  // Initialize the form with default values or values from the customer
  const form = useForm<CustomerFormType>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: customer
      ? {
          customer_type: customer.customer_type || 'residential',
          name: customer.name ?? "",
          last_name: customer.last_name ?? "",
          cell_numbers:
            customer.cell_numbers?.map((n: string) => ({ value: n })) ||
            [{ value: "" }],
          emails:
            customer.emails?.map((e: string) => ({ value: e })) ||
            [{ value: "" }],
          business_name: customer.business_name || "",
          vat_number: customer.vat_number || "",
          business_phone: customer.business_phone || "",
          business_email: customer.business_email || "",
          representative_first_name: customer.representative_first_name || "",
          representative_last_name: customer.representative_last_name || "",
          representative_position: customer.representative_position || "",
          representative_phone: customer.representative_phone || "",
          representative_email: customer.representative_email || "",
          street_address: customer.street_address || "",
          suburb: customer.suburb || "",
          city: customer.city || "",
          province_state: customer.province_state || "",
          postal_code: customer.postal_code || "",
          lead_source: customer.lead_source || "",
          lead_owner_id: customer.lead_owner_id || user?.id || null,
          lead_source_description: customer.lead_source_description || "",
          tags: customer.tags || [],
          referred_by_id: customer.referred_by_id || null,
        }
      : {
          customer_type: 'residential',
          name: "",
          last_name: "",
          cell_numbers: [{ value: "" }],
          emails: [{ value: "" }],
          business_name: "",
          vat_number: "",
          business_phone: "",
          business_email: "",
          representative_first_name: "",
          representative_last_name: "",
          representative_position: "",
          representative_phone: "",
          representative_email: "",
          street_address: "",
          suburb: "",
          city: "",
          province_state: "",
          postal_code: "",
          lead_source: "",
          lead_owner_id: user?.id || null,
          lead_source_description: "",
          tags: [],
          referred_by_id: null,
        },
  });

  // Create field arrays for cell numbers and emails
  const {
    fields: cellFields,
    append: appendCell,
    remove: removeCell,
  } = useFieldArray({ name: "cell_numbers", control: form.control });
  
  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({ name: "emails", control: form.control });

  // Watch the lead source to conditionally render fields
  const watchedLeadSource = form.watch("lead_source");

  // Fetch company members
  useEffect(() => {
    const fetchCompanyMembers = async () => {
      if (!profile?.company_id) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .eq("company_id", profile.company_id);

        if (error) {
          console.error("Error fetching company members:", error);
          return;
        }

        setCompanyMembers(
          data?.map((profile) => ({
            user_id: profile.id,
            first_name: profile.first_name,
            last_name: profile.last_name,
          })) || []
        );
      } catch (error) {
        console.error("Error in fetchCompanyMembers:", error);
      }
    };

    if (profile?.company_id) {
      fetchCompanyMembers();
    }
  }, [profile?.company_id]);

  // Check for duplicate customers
  const checkForDuplicateCustomer = async (values: CustomerFormType) => {
    try {
      const emails = values.emails.map(e => e.value).filter(Boolean);
      const cellNumbers = values.cell_numbers.map(c => c.value).filter(Boolean);
      
      if (!emails.length && !cellNumbers.length) return null;
      
      let query = supabase.from("customers").select("id, name, last_name");
      
      if (emails.length) {
        query = query.or(`emails.cs.{${emails.join(",")}}`);
      }
      
      if (cellNumbers.length) {
        query = query.or(`cell_numbers.cs.{${cellNumbers.join(",")}}`);
      }
      
      if (customer?.id) {
        query = query.neq("id", customer.id);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error checking for duplicate customers:", error);
        return null;
      }
      
      if (data && data.length > 0) {
        return {
          id: data[0].id,
          name: data[0].name,
          lastName: data[0].last_name,
        };
      }
      
      return null;
    } catch (error) {
      console.error("Error in checkForDuplicateCustomer:", error);
      return null;
    }
  };

  // Function to prepare form for duplicate customer editing
  const prepareFormForDuplicateEdit = async (customerId: string) => {
    try {
      // Fetch the existing customer data
      const { data: existingCustomer, error } = await supabase
        .from("customers")
        .select("*")
        .eq("id", customerId)
        .single();
      
      if (error) {
        console.error("Error fetching existing customer:", error);
        return;
      }
      
      // Get the current form values
      const currentValues = form.getValues();
      
      // Prepare the merged data
      const mergedData = {
        // Keep the existing customer's core info
        ...existingCustomer,
        
        // Merge contact information
        cell_numbers: [
          ...new Set([
            ...(existingCustomer.cell_numbers || []),
            ...currentValues.cell_numbers.map(c => c.value).filter(Boolean)
          ])
        ],
        emails: [
          ...new Set([
            ...(existingCustomer.emails || []),
            ...currentValues.emails.map(e => e.value).filter(Boolean)
          ])
        ],
        
        // Merge tags
        tags: [
          ...new Set([
            ...(existingCustomer.tags || []),
            ...(currentValues.tags || [])
          ])
        ]
      };
      
      console.log("Prepared merged data for duplicate customer:", mergedData);
      
      return mergedData;
    } catch (error) {
      console.error("Error preparing form for duplicate editing:", error);
    }
  };

  // Handle form submission
  const onSubmit = async (values: CustomerFormType) => {
    if (!guardWrite('create or edit customers')) return;
    setIsSubmitting(true);
    
    try {
      // Check for duplicate customers
      const duplicate = await checkForDuplicateCustomer(values);
      
      if (duplicate) {
        setDuplicateRecord(duplicate);
        setIsSubmitting(false);
        return;
      }
      
      // Prepare data for submission
      const customerData = {
        customer_type: values.customer_type,
        name: values.name,
        last_name: values.last_name,
        cell_numbers: values.cell_numbers.map((c) => c.value).filter(Boolean),
        emails: values.emails.map((e) => e.value).filter(Boolean),
        business_name: values.business_name || null,
        vat_number: values.vat_number || null,
        business_phone: values.business_phone || null,
        business_email: values.business_email || null,
        representative_first_name: values.representative_first_name || null,
        representative_last_name: values.representative_last_name || null,
        representative_position: values.representative_position || null,
        representative_phone: values.representative_phone || null,
        representative_email: values.representative_email || null,
        street_address: values.street_address,
        suburb: values.suburb,
        city: values.city,
        province_state: values.province_state,
        postal_code: values.postal_code,
        lead_source: values.lead_source,
        lead_owner_id: values.lead_owner_id,
        lead_source_description:
          values.lead_source === "Other" ? values.lead_source_description : null,
        tags: values.tags,
        referred_by_id: values.lead_source === "Word of Mouth" ? values.referred_by_id : null,
      };
      
      let result;
      
      if (customer) {
        // Update existing customer
        result = await supabase
          .from("customers")
          .update({
            ...customerData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", customer.id);
      } else {
        // Create new customer
        result = await supabase.from("customers").insert({
          ...customerData,
          created_by: user?.id,
        });
      }
      
      if (result.error) {
        console.error("Error saving customer:", result.error);
        notify.error(result.error);
        return;
      }
      
      notify.success(`Customer ${customer ? "updated" : "created"} successfully`);
      
      onSave();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      notify.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    companyMembers,
    cellFields,
    emailFields,
    appendCell,
    removeCell,
    appendEmail,
    removeEmail,
    watchedLeadSource,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting,
    duplicateRecord,
    setDuplicateRecord,
    prepareFormForDuplicateEdit,
  };
}
